import React, { useState, useEffect } from 'react';
import { Download, Upload, AlertTriangle, CheckCircle, Database, Users, Ship, FileText, CreditCard, Anchor, Settings, Calendar, RefreshCw, Shield, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

// Spinner stili
const spinnerStyle = {
  animation: 'spin 1s linear infinite'
};

const BackupSettings = () => {
  const [loading, setLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [lastBackupInfo, setLastBackupInfo] = useState(null);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [restorePreview, setRestorePreview] = useState(null);
  
  // Veri doğrulama state'leri
  const [integrityData, setIntegrityData] = useState(null);
  const [checkingIntegrity, setCheckingIntegrity] = useState(false);
  const [validatingData, setValidatingData] = useState(false);
  const [validationResult, setValidationResult] = useState(null);

  // Sayfa yüklendiğinde veri tutarlılığını kontrol et
  useEffect(() => {
    checkDataIntegrity();
  }, []);

  // Veri tutarlılığı kontrolü
  const checkDataIntegrity = async () => {
    setCheckingIntegrity(true);
    try {
      const response = await api.get('/admin/data-integrity-check');
      setIntegrityData(response.data);
    } catch (error) {
      console.error('Veri tutarlılığı kontrolü başarısız:', error);
    } finally {
      setCheckingIntegrity(false);
    }
  };

  // Veri doğrulama ve düzeltme
  const handleValidateData = async () => {
    setValidatingData(true);
    setValidationResult(null);
    try {
      const response = await api.post('/admin/validate-data');
      setValidationResult(response.data.validation_report);
      
      if (response.data.validation_report.is_consistent) {
        toast.success('Veri tutarlılığı doğrulandı! Tüm veriler senkronize.');
      } else {
        toast.info(`Düzeltmeler yapıldı: ${response.data.validation_report.missing_records_created} eksik kayıt oluşturuldu, ${response.data.validation_report.orphaned_records_deleted} yetim kayıt silindi.`);
      }
      
      // Tutarlılık bilgisini güncelle
      await checkDataIntegrity();
    } catch (error) {
      toast.error('Veri doğrulama işlemi başarısız');
    } finally {
      setValidatingData(false);
    }
  };

  // Yedek al
  const handleBackup = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/backup');
      const backupData = response.data;
      
      // JSON dosyası oluştur ve indir
      const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      link.href = url;
      link.download = `rota-maritime-backup-${timestamp}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setLastBackupInfo(backupData.backup_info);
      toast.success(`Yedek başarıyla oluşturuldu! (${backupData.backup_info.total_records} kayıt)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Yedek oluşturulamadı');
    } finally {
      setLoading(false);
    }
  };

  // Dosya seçildiğinde
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.json')) {
      toast.error('Lütfen JSON formatında bir yedek dosyası seçin');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        
        if (!data.backup_info || !data.collections) {
          toast.error('Geçersiz yedek dosyası formatı');
          return;
        }
        
        setSelectedFile(file);
        setRestorePreview({
          data: data,
          info: data.backup_info,
          collections: Object.entries(data.collections).map(([name, items]) => ({
            name,
            count: items?.length || 0
          }))
        });
        setShowRestoreConfirm(true);
      } catch (err) {
        toast.error('Dosya okunamadı veya geçersiz JSON formatı');
      }
    };
    reader.readAsText(file);
  };

  // Geri yükleme işlemi
  const handleRestore = async () => {
    if (!restorePreview?.data) return;
    
    setRestoreLoading(true);
    try {
      const response = await api.post('/admin/restore', restorePreview.data);
      
      // Veri doğrulama sonucunu göster
      const validationReport = response.data.data_validation;
      if (validationReport) {
        if (validationReport.is_consistent) {
          toast.success(`Yedek başarıyla geri yüklendi! (${response.data.total_restored} kayıt) - Veri tutarlılığı doğrulandı ✓`);
        } else {
          toast.info(`Yedek geri yüklendi (${response.data.total_restored} kayıt). ${validationReport.missing_records_created} eksik kayıt otomatik oluşturuldu.`);
        }
      } else {
        toast.success(`Yedek başarıyla geri yüklendi! (${response.data.total_restored} kayıt)`);
      }
      
      setShowRestoreConfirm(false);
      setSelectedFile(null);
      setRestorePreview(null);
      
      // Sayfayı yenile
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Geri yükleme başarısız');
    } finally {
      setRestoreLoading(false);
    }
  };

  // Koleksiyon isimlerini Türkçeleştir
  const getCollectionLabel = (name) => {
    const labels = {
      users: 'Kullanıcılar',
      clients: 'Müşteriler',
      vessels: 'Gemiler',
      proformas: 'Proformalar',
      invoices: 'Faturalar',
      payments: 'Ödemeler',
      port_calls: 'Liman Çağrıları',
      anchorage: 'Demirsahası Takip',
      motor_launches: 'Motor Seferleri',
      documents: 'Belgeler',
      settings: 'Ayarlar',
      counters: 'Sayaçlar',
      document_templates: 'Belge Şablonları',
      exchange_rates: 'Döviz Kurları'
    };
    return labels[name] || name;
  };

  // Koleksiyon ikonu
  const getCollectionIcon = (name) => {
    const icons = {
      users: Users,
      clients: Users,
      vessels: Ship,
      proformas: FileText,
      invoices: FileText,
      payments: CreditCard,
      port_calls: Anchor,
      anchorage: Anchor,
      motor_launches: Ship,
      documents: FileText,
      settings: Settings,
      counters: Database,
      document_templates: FileText,
      exchange_rates: CreditCard
    };
    const Icon = icons[name] || Database;
    return <Icon size={16} />;
  };

  return (
    <div className="settings-section" data-testid="backup-settings">
      <h3 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Database size={24} />
        Yedekleme ve Geri Yükleme
      </h3>

      {/* Veri Tutarlılığı Bölümü */}
      <div style={{ 
        background: integrityData?.is_consistent ? '#f0fdf4' : '#fefce8', 
        borderRadius: '12px', 
        padding: '1.5rem',
        marginBottom: '1.5rem',
        border: `1px solid ${integrityData?.is_consistent ? '#86efac' : '#fde047'}`
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
          <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Shield size={20} style={{ color: integrityData?.is_consistent ? '#059669' : '#f59e0b' }} />
            Veri Tutarlılığı
          </h4>
          <button
            className="btn btn-secondary"
            onClick={checkDataIntegrity}
            disabled={checkingIntegrity}
            style={{ padding: '0.4rem 0.75rem', fontSize: '0.8rem' }}
          >
            {checkingIntegrity ? <Loader2 size={14} style={spinnerStyle} /> : <RefreshCw size={14} />}
            <span style={{ marginLeft: '0.25rem' }}>Kontrol Et</span>
          </button>
        </div>
        
        {checkingIntegrity ? (
          <div style={{ textAlign: 'center', padding: '1rem', color: '#64748b' }}>
            <Loader2 size={24} style={{ ...spinnerStyle, marginBottom: '0.5rem' }} />
            <p style={{ margin: 0 }}>Kontrol ediliyor...</p>
          </div>
        ) : integrityData ? (
          <>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem', 
              marginBottom: '1rem',
              fontSize: '1rem',
              fontWeight: 600,
              color: integrityData.is_consistent ? '#059669' : '#d97706'
            }}>
              {integrityData.is_consistent ? (
                <>
                  <CheckCircle size={20} />
                  <span>Tüm veriler tutarlı</span>
                </>
              ) : (
                <>
                  <AlertTriangle size={20} />
                  <span>Veri tutarsızlığı tespit edildi</span>
                </>
              )}
            </div>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '1rem',
              marginBottom: '1rem'
            }}>
              {/* Faturalar */}
              <div style={{ 
                background: 'white', 
                padding: '1rem', 
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>Faturalar</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1e40af' }}>
                  ${integrityData.invoices?.total_sales?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                  {integrityData.invoices?.count} adet fatura
                </div>
              </div>
              
              {/* İşlem Takip */}
              <div style={{ 
                background: 'white', 
                padding: '1rem', 
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>İşlem Takip</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#7c3aed' }}>
                  ${integrityData.transaction_tracking?.total_sales?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                  {integrityData.transaction_tracking?.count} adet kayıt
                </div>
              </div>
              
              {/* Fark */}
              <div style={{ 
                background: 'white', 
                padding: '1rem', 
                borderRadius: '8px',
                border: `1px solid ${integrityData.comparison?.difference === 0 ? '#86efac' : '#fca5a5'}`
              }}>
                <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>Fark</div>
                <div style={{ 
                  fontSize: '1.25rem', 
                  fontWeight: 700, 
                  color: integrityData.comparison?.difference === 0 ? '#059669' : '#dc2626' 
                }}>
                  ${Math.abs(integrityData.comparison?.difference || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                  {integrityData.comparison?.status}
                </div>
              </div>
            </div>
            
            {!integrityData.is_consistent && (
              <button
                className="btn btn-warning"
                onClick={handleValidateData}
                disabled={validatingData}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                {validatingData ? (
                  <>
                    <Loader2 size={18} style={spinnerStyle} />
                    Düzeltiliyor...
                  </>
                ) : (
                  <>
                    <RefreshCw size={18} />
                    Otomatik Düzelt
                  </>
                )}
              </button>
            )}
            
            {/* Doğrulama Sonucu */}
            {validationResult && (
              <div style={{ 
                marginTop: '1rem', 
                padding: '1rem', 
                background: validationResult.is_consistent ? '#f0fdf4' : '#fefce8',
                borderRadius: '8px',
                border: `1px solid ${validationResult.is_consistent ? '#86efac' : '#fde047'}`
              }}>
                <h5 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem' }}>Son Doğrulama Sonucu:</h5>
                <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.85rem', color: '#374151' }}>
                  <li>Kontrol edilen fatura: {validationResult.invoices_checked}</li>
                  <li>Oluşturulan eksik kayıt: {validationResult.missing_records_created}</li>
                  <li>Silinen yetim kayıt: {validationResult.orphaned_records_deleted}</li>
                  <li>Sorunlu fatura sayısı: {validationResult.invoices_with_issues?.length || 0}</li>
                </ul>
              </div>
            )}
          </>
        ) : (
          <p style={{ color: '#64748b', margin: 0 }}>Veri tutarlılığı bilgisi yüklenemedi.</p>
        )}
      </div>

      {/* Bilgi Kutusu */}
      <div style={{ 
        background: '#f0f9ff', 
        border: '1px solid #bae6fd', 
        borderRadius: '8px', 
        padding: '1rem',
        marginBottom: '1.5rem'
      }}>
        <p style={{ margin: 0, fontSize: '0.9rem', color: '#0369a1' }}>
          <strong>📋 Yedeklenen Veriler:</strong> Müşteriler, Gemiler, Proformalar, Liman Çağrıları, 
          Demirsahası Takip, Motor Seferleri, Faturalar, Ödemeler, Belgeler, Ayarlar ve Kullanıcılar
        </p>
        <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem', color: '#0369a1' }}>
          <strong>🔄 Otomatik Doğrulama:</strong> Yedek geri yüklendiğinde veri tutarlılığı otomatik olarak kontrol edilir ve gerekli düzeltmeler yapılır.
        </p>
      </div>

      {/* Yedek Al Bölümü */}
      <div style={{ 
        background: '#f8fafc', 
        borderRadius: '12px', 
        padding: '1.5rem',
        marginBottom: '1.5rem',
        border: '1px solid #e2e8f0'
      }}>
        <h4 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Download size={20} className="text-blue-600" />
          Yedek Al
        </h4>
        <p style={{ color: '#64748b', marginBottom: '1rem', fontSize: '0.9rem' }}>
          Tüm sistem verilerinin yedeğini JSON formatında indirin. Bu dosyayı güvenli bir yerde saklayın.
        </p>
        <button
          className="btn btn-primary"
          onClick={handleBackup}
          disabled={loading}
          data-testid="backup-btn"
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          {loading ? (
            <>
              <span className="spinner-sm"></span>
              Yedek Oluşturuluyor...
            </>
          ) : (
            <>
              <Download size={18} />
              Yedek Al
            </>
          )}
        </button>
        
        {lastBackupInfo && (
          <div style={{ 
            marginTop: '1rem', 
            padding: '0.75rem', 
            background: '#f0fdf4', 
            borderRadius: '8px',
            border: '1px solid #86efac',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <CheckCircle size={18} className="text-green-600" />
            <span style={{ fontSize: '0.875rem' }}>
              Son yedek: {new Date(lastBackupInfo.created_at).toLocaleString('tr-TR')} 
              ({lastBackupInfo.total_records} kayıt)
            </span>
          </div>
        )}
      </div>

      {/* Geri Yükle Bölümü */}
      <div style={{ 
        background: '#fefce8', 
        borderRadius: '12px', 
        padding: '1.5rem',
        border: '1px solid #fde047'
      }}>
        <h4 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Upload size={20} className="text-amber-600" />
          Geri Yükle
        </h4>
        <div style={{ 
          background: '#fef2f2', 
          border: '1px solid #fca5a5', 
          borderRadius: '8px', 
          padding: '0.75rem',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.5rem'
        }}>
          <AlertTriangle size={18} className="text-red-600" style={{ marginTop: '2px', flexShrink: 0 }} />
          <span style={{ fontSize: '0.875rem', color: '#dc2626' }}>
            <strong>Dikkat:</strong> Geri yükleme işlemi mevcut TÜM verileri silecek ve yedekteki verilerle değiştirecektir. 
            Bu işlem geri alınamaz!
          </span>
        </div>
        
        <input
          type="file"
          accept=".json"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          id="restore-file-input"
          data-testid="restore-file-input"
        />
        <label
          htmlFor="restore-file-input"
          className="btn btn-warning"
          style={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            cursor: 'pointer'
          }}
        >
          <Upload size={18} />
          Yedek Dosyası Seç
        </label>
      </div>

      {/* Geri Yükleme Onay Modalı */}
      {showRestoreConfirm && restorePreview && (
        <div className="modal-overlay" onClick={() => !restoreLoading && setShowRestoreConfirm(false)}>
          <div 
            className="modal" 
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '600px', maxHeight: '80vh', overflow: 'auto' }}
          >
            <div className="modal-header">
              <h2 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <AlertTriangle className="text-amber-500" />
                Geri Yükleme Onayı
              </h2>
              <button 
                className="modal-close" 
                onClick={() => !restoreLoading && setShowRestoreConfirm(false)}
                disabled={restoreLoading}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {/* Yedek Bilgileri */}
              <div style={{ 
                background: '#f8fafc', 
                borderRadius: '8px', 
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <h4 style={{ marginBottom: '0.75rem', fontSize: '0.95rem' }}>Yedek Bilgileri</h4>
                <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                  <p><strong>Dosya:</strong> {selectedFile?.name}</p>
                  <p><strong>Oluşturulma:</strong> {new Date(restorePreview.info.created_at).toLocaleString('tr-TR')}</p>
                  <p><strong>Oluşturan:</strong> {restorePreview.info.created_by_name || restorePreview.info.created_by}</p>
                  <p><strong>Versiyon:</strong> {restorePreview.info.version}</p>
                </div>
              </div>

              {/* Koleksiyonlar */}
              <div style={{ marginBottom: '1rem' }}>
                <h4 style={{ marginBottom: '0.75rem', fontSize: '0.95rem' }}>Geri Yüklenecek Veriler</h4>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(2, 1fr)', 
                  gap: '0.5rem',
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}>
                  {restorePreview.collections.map((col) => (
                    <div 
                      key={col.name}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.5rem',
                        padding: '0.5rem 0.75rem',
                        background: col.count > 0 ? '#f0fdf4' : '#f8fafc',
                        borderRadius: '6px',
                        fontSize: '0.875rem'
                      }}
                    >
                      {getCollectionIcon(col.name)}
                      <span style={{ flex: 1 }}>{getCollectionLabel(col.name)}</span>
                      <span style={{ 
                        fontWeight: 600, 
                        color: col.count > 0 ? '#059669' : '#94a3b8' 
                      }}>
                        {col.count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Uyarı */}
              <div style={{ 
                background: '#fef2f2', 
                border: '1px solid #fca5a5', 
                borderRadius: '8px', 
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <p style={{ margin: 0, color: '#dc2626', fontWeight: 600 }}>
                  ⚠️ Bu işlem mevcut tüm verileri silecek ve yedekteki verilerle değiştirecektir. 
                  Devam etmek istediğinizden emin misiniz?
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowRestoreConfirm(false)}
                disabled={restoreLoading}
              >
                İptal
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleRestore}
                disabled={restoreLoading}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                {restoreLoading ? (
                  <>
                    <span className="spinner-sm"></span>
                    Geri Yükleniyor...
                  </>
                ) : (
                  <>
                    <Upload size={18} />
                    Evet, Geri Yükle
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackupSettings;
